import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {AudienceCategory, DuplicateSurveyRequest, Survey} from "../../surveys.model";
import { Subject } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { NgIf, AsyncPipe } from '@angular/common';
import { FlexModule } from '@angular/flex-layout';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {SurveyService} from "../../survey.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";

/**
 * Duplicate Survey Dialog Component
 *
 * Advanced Rules:
 *
 * Data Passing:
 * - The dialog receives the source survey via MAT_DIALOG_DATA and optionally the clearOnDestroy$ observable from the parent.
 *
 * Pre-population & Optional Override:
 * - The form is pre-populated with the source survey’s name, audience, startDate, endDate, and allowMultiSubmission.
 *
 * Dirty Checks on Submit:
 * - In onSubmit(), only controls marked dirty (i.e. changed by the user) are included in the DuplicateSurveyRequest.
 *   For any control not changed, null is sent, so that the backend’s firstNonNull logic uses the original values.
 *
 * Loading Indicator:
 * - The service call is wrapped using loadingService.withLoader, and a progress bar is shown while loading.
 */
@Component({
  selector: 'app-duplicate-survey-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatSlideToggleModule,
    MatButtonModule,
    NgIf,
    FlexModule,
    MatSelectModule,
    AsyncPipe,
    MatProgressBarModule
  ],
  templateUrl: './duplicate-survey-dialog.component.html'
})
export class DuplicateSurveyDialogComponent implements OnInit, OnDestroy {
  private _clearOnDestroy$ = new Subject<void>();

  surveyForm: FormGroup;
  protected loading$ = this.loadingService.loading$;
  protected readonly AudienceCategory = AudienceCategory;

  constructor(
    private fb: FormBuilder,
    private surveyService: SurveyService,
    private loadingService: LoadingService,
    private dialogRef: MatDialogRef<DuplicateSurveyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { survey: Survey, clearOnDestroy$?: Subject<void> }
  ) {
    // Prepopulate all fields with the source survey’s values.
    this.surveyForm = this.fb.group({
      name: [data.survey.name, Validators.required],
      audience: [data.survey.audience, Validators.required],
      startDate: [data.survey.startDate, Validators.required],
      endDate: [data.survey.endDate]
    });
  }

  ngOnInit(): void {
    if (this.data.clearOnDestroy$) {
      this._clearOnDestroy$ = this.data.clearOnDestroy$;
    }
  }

  ngOnDestroy(): void {
    this._clearOnDestroy$.next();
    this._clearOnDestroy$.complete();
  }

  onSubmit(): void {
    if (this.surveyForm.valid) {
      const form = this.surveyForm;
      // Build the DuplicateSurveyRequest.
      // For each field, if the control is dirty (changed), include its value; otherwise send null.
      const request: DuplicateSurveyRequest = {
        templateUuid: this.data.survey.uuid!,
        name: form.get('name')!.dirty ? form.get('name')!.value : null,
        audience: form.get('audience')!.dirty ? form.get('audience')!.value : null,
        startDate: form.get('startDate')!.dirty ? form.get('startDate')!.value : null,
        endDate: form.get('endDate')!.dirty ? form.get('endDate')!.value : null,
      };

      this.surveyService.duplicateSurvey(request)
        .pipe(this.loadingService.withLoader(this._clearOnDestroy$))
        .subscribe({
          next: (survey: Survey) => {
            this.dialogRef.close(survey);
          },
          error: err => {
            console.error('Error duplicating survey:', err);
          }
        });
    }
  }

  onCancel(): void {
    this.ngOnDestroy();
    this.dialogRef.close();
  }
}
